<template>
  <section class="quick-control-panel" :class="{ embedded }">
    <!-- 快捷控制标题与当前飞机状态 -->
    <div v-if="showTabs" class="panel-heading gs-section-heading">
      <div class="control-tabs ui-tab-list" :class="{ single: !hasPeripheralModule }">
        <UiTabButton :active="activePanel === 'flight'" @click="switchPanel('flight')">{{ $t("飞行控制") }}</UiTabButton>
        <UiTabButton v-if="hasPeripheralModule" :active="activePanel === 'peripheral'" @click="switchPanel('peripheral')">{{ $t("外设控制") }}</UiTabButton>
      </div>
    </div>

    <!-- 飞机基础飞行控制 -->
    <transition name="control-deal" mode="out-in" :duration="{ enter: 900, leave: 120 }">
    <div v-if="activePanel === 'flight'" key="flight" class="control-grid control-panel-view">
      <UiButton variant="control" tone="warning" class="control-button" :disabled="disabled || !plane" @click="confirmUnlock">
        <i class="iconfont icon-jiesuo"></i><span>{{ $t("解锁") }}</span>
      </UiButton>
      <UiButton variant="control" tone="danger" class="control-button" :disabled="disabled || !plane || isAutoGoodsDown" @click="openLockSlider">
        <i class="iconfont icon-suoding"></i><span>{{ $t("加锁") }}</span>
      </UiButton>
      <UiButton variant="control" tone="warning" class="control-button" :disabled="disabled || !plane" @click="sendCommand('{loiterMode:1}', '悬停')">
        <i class="iconfont icon-fengzheng1"></i><span>{{ $t("悬停") }}</span>
      </UiButton>
      <UiButton variant="control" class="control-button" :disabled="disabled || !plane" @click="confirmFlightCommand('rtl')">
        <i class="iconfont icon-yijianfanhang"></i><span>{{ $t("返航") }}</span>
      </UiButton>
      <UiButton variant="control" class="control-button" :disabled="disabled || !plane" @click="openTakeoffDialog">
        <i class="iconfont icon-yangshi_icon_tongyong_departure"></i><span>{{ $t("起飞") }}</span>
      </UiButton>
      <UiButton variant="control" class="control-button" :disabled="disabled || !plane || resumePending" @click="confirmFlightCommand('resume')">
        <i class="iconfont icon-duandianxufei"></i><span>{{ $t("复航") }}</span>
      </UiButton>
      <UiButton variant="control" tone="danger" class="control-button" :disabled="disabled || !plane" @click="confirmFlightCommand('land')">
        <i class="iconfont icon-yangshi_icon_tongyong_arriving"></i><span>{{ $t("降落") }}</span>
      </UiButton>
      <UiButton variant="control" class="control-button" :disabled="disabled || !plane" @click="confirmClearOscillation">
        <i class="iconfont icon-zhendong"></i><span>{{ $t("消摆") }}</span>
      </UiButton>
    </div>

    <!-- 飞机挂载、摄像头及喇叭控制 -->
    <GroundStationPeripheralControlPanel v-else key="peripheral" class="control-panel-view" :plane="plane" />
    </transition>

    <GroundStationLockDialog v-model="showLockSlider" :plane="plane" :disabled="disabled" @confirm="sendCommand('{lock:1}', '加锁飞机')" />

    <!-- 起飞高度设置沿用原控制组件的 1—100 米范围 -->
    <UiDialog v-model="takeoffDialogVisible" :title="$t('高度设置')" width="340px" top="30vh" custom-class="ground-station-takeoff-dialog">
      <div class="takeoff-setting">
        <span>{{ $t("起飞高度") }}</span>
        <UiNumberInput v-model="takeoffValue" :unit="$t('米')" :min="1" :max="100" :step="1" size="small" />
      </div>
      <span slot="footer">
        <UiButton action="save" @click="confirmTakeoff">{{ $t("确认起飞") }}</UiButton>
        <UiButton action="cancel" @click="takeoffDialogVisible = false">{{ $t("取消") }}</UiButton>
      </span>
    </UiDialog>
  </section>
</template>

<script>
import { t } from '@/i18n'
import mqtt from '@/utils/mqtt'
import { speakText } from '@/utils/index'
import { executeConfirmedMission } from '@/utils/missionSafety'
import { confirmFlightCommand as openFlightCommandConfirm } from '@/utils/flightCommandConfirm'
import { hasPeripheralModule as planeHasPeripheralModule } from '@/utils/planeModules'
import GroundStationPeripheralControlPanel from '@/components/ground-station/GroundStationPeripheralControlPanel'
import GroundStationLockDialog from '@/components/ground-station/GroundStationLockDialog'

export default {
  name: 'GroundStationQuickControlPanel',
  components: { GroundStationPeripheralControlPanel, GroundStationLockDialog },
  props: {
    plane: { type: Object, default: null },
    showTabs: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false }
  },
  data () {
    return {
      activePanel: 'flight',
      resumePending: false,
      takeoffDialogVisible: false,
      takeoffValue: 2,
      showLockSlider: false
    }
  },
  computed: {
    hasPeripheralModule () { return planeHasPeripheralModule(this.plane) },
    isAutoGoodsDown () {
      const value = this.plane && this.plane.planeState ? this.plane.planeState.isAutoGoodsDown : null
      return value === true || value === 1 || value === '1'
    }
  },
  watch: {
    plane () {
      this.closeLockSlider()
      this.takeoffDialogVisible = false
      if (!this.hasPeripheralModule) this.activePanel = 'flight'
    }
  },
  methods: {
    switchPanel (panel) {
      if (panel === 'peripheral' && !this.hasPeripheralModule) return
      if (this.activePanel === panel) return
      this.activePanel = panel
      speakText(panel === 'flight' ? '飞机控制' : '外设控制')
    },
    publishCommand (command) {
      if (this.disabled || !this.plane) {
        this.$message.warning(t('请先选择飞机'))
        return false
      }
      return mqtt.publishCommand(this.plane, command)
    },
    writeTerminalLog (content) {
      if (!this.plane || !content) return
      this.$store.dispatch('fetchLog', {
        sourceType: 'terminal',
        planeId: this.plane.id,
        planeName: this.plane.name,
        content
      })
    },
    sendCommand (command, description) {
      if (!this.publishCommand(command)) return
      this.writeTerminalLog(description)
      speakText(description)
    },
    confirmUnlock () {
      if (!this.plane) return
      this.$confirm(t('确认对飞机解锁？请确保飞行环境安全。'), t('解锁操作'), {
        confirmButtonText: t('确定'),
        cancelButtonText: t('取消'),
        type: 'warning'
      }).then(() => this.sendCommand('{unlock:1}', '解锁飞机')).catch(() => {})
    },
    confirmFlightCommand (type) {
      if (!this.plane) return
      const plane = this.plane
      openFlightCommandConfirm(this, type, async (command, description) => {
        if (this.plane !== plane) return
        if (type !== 'resume') return this.sendCommand(command, description)
        if (this.resumePending) return
        this.resumePending = true
        try {
          await executeConfirmedMission(plane, null, () => this.plane === plane && !this._isDestroyed && !this._isBeingDestroyed, true)
          if (this.plane !== plane) return
          this.writeTerminalLog('飞机已接受本次任务复航指令')
          speakText(description)
        } catch (error) {
          await Promise.allSettled(['fetchPaidOrderList', 'fetchSiteList'].map(action => this.$store.dispatch(action)))
          this.$message.error(t(error.message || '任务核对失败，未复航'))
        } finally {
          this.resumePending = false
        }
      })
    },
    confirmClearOscillation () {
      if (!this.plane) return
      this.$confirm(t('确认对当前飞机执行消摆？请先确认飞机及周围环境安全。'), t('消摆操作'), {
        confirmButtonText: t('确认消摆'),
        cancelButtonText: t('取消'),
        type: 'warning'
      }).then(() => {
        const ok = mqtt.publishCommand(this.plane, '{"clearOscillation":1}')
        if (!ok) {
          this.$message.error(t('指令服务器未连接，消摆指令发送失败'))
          return
        }
        this.writeTerminalLog('执行消摆')
        speakText('消摆')
      }).catch(() => {})
    },
    openTakeoffDialog () {
      if (!this.plane) return
      this.takeoffDialogVisible = true
      speakText('设置起飞高度')
    },
    confirmTakeoff () {
      this.sendCommand(`{guidedMode:{alt:${this.takeoffValue}}}`, `起飞至 ${this.takeoffValue} 米`)
      this.takeoffDialogVisible = false
    },
    openLockSlider () {
      if (this.disabled || !this.plane || this.isAutoGoodsDown) return
      this.showLockSlider = true
    },
    closeLockSlider () { this.showLockSlider = false }
  }
}
</script>

<style lang="scss" scoped>
.quick-control-panel { padding: 0 var(--ui-space-sm) var(--ui-space-md); }
.quick-control-panel.embedded { padding: 0; }
.panel-heading { width: 100%; min-height: 44px; display: flex; align-items: center; margin-bottom: var(--ui-space-sm); padding: 0; color: var(--ui-text-secondary); font-size: var(--gs-font-heading, 12px); letter-spacing: var(--gs-letter-heading, 1.3px); }
.control-tabs { width: 100%; display: grid; grid-template-columns: 1fr 1fr; }.control-tabs button { font-size: var(--gs-font-heading, 12px); font-weight: var(--gs-weight-medium, 500); line-height: 1.2; letter-spacing: .45px; }
.control-tabs.single { grid-template-columns: 1fr; }
.control-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--ui-space-sm); padding: var(--ui-space-sm); }
.control-button.wide { grid-column: 1 / -1; }
.control-deal-enter-active { animation: control-panel-deal-in .28s ease-out both; }
.control-deal-leave-active { animation: control-panel-deal-out .12s ease-in both; }
.control-deal-enter-active .control-grid > .lock-control-slot,
.control-deal-enter-active .control-grid > .control-button { animation: control-card-deal-in .36s cubic-bezier(.2, .72, .24, 1) both; }
.control-deal-enter-active .control-grid > :nth-child(2) { animation-delay: 80ms; }
.control-deal-enter-active .control-grid > :nth-child(3) { animation-delay: 160ms; }
.control-deal-enter-active .control-grid > :nth-child(4) { animation-delay: 240ms; }
.control-deal-enter-active .control-grid > :nth-child(5) { animation-delay: 320ms; }
.control-deal-enter-active .control-grid > :nth-child(6) { animation-delay: 400ms; }
.control-deal-enter-active .control-grid > :nth-child(7) { animation-delay: 480ms; }
.control-deal-enter-active .control-grid > :nth-child(8) { animation-delay: 560ms; }
.control-deal-enter-active::v-deep .device-section { animation: control-card-deal-in .38s cubic-bezier(.2, .72, .24, 1) both; }
.control-deal-enter-active::v-deep .device-section:nth-child(2) { animation-delay: 120ms; }
.control-deal-enter-active::v-deep .device-section:nth-child(3) { animation-delay: 240ms; }
.control-deal-enter-active::v-deep .device-section:nth-child(4) { animation-delay: 360ms; }
.takeoff-setting { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: var(--ui-space-sm); }
@keyframes control-panel-deal-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes control-panel-deal-out { to { opacity: 0; transform: translateY(3px); } }
@keyframes control-card-deal-in { from { opacity: 0; transform: translate3d(-18px, -12px, 0) rotate(-3deg) scale(.94); } to { opacity: 1; transform: translate3d(0, 0, 0) rotate(0) scale(1); } }
</style>
